import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  BulletOptimizationState,
  BulletStatus,
  BulletActionRequest,
  BulletChatRequest,
  OptimizeResumeStatus,
} from "../data/interface/InterviewHubInterface";
import InterviewHubApiService from "../service/InterviewHubApiService";
import Loader from "../../../utils/Utils";

interface RewriteChatModalProps {
  show: boolean;
  onHide: () => void;
  bulletState: BulletOptimizationState;
  sessionId: string;
  service: InterviewHubApiService;
  onUpdate: (newState: BulletOptimizationState) => void;
}

const RewriteChatModal: React.FC<RewriteChatModalProps> = ({
  show,
  onHide,
  bulletState,
  sessionId,
  service,
  onUpdate,
}) => {
  const [mode, setMode] = useState<"initial" | "edit" | "coaching" | "completed">("initial");
  const [userEdit, setUserEdit] = useState(bulletState.ai_suggestion || bulletState.original_bullet);
  const [userInput, setUserInput] = useState("");
  const [localMessages, setLocalMessages] = useState<{ role: "bot" | "user"; content: string; isAffirmativePrompt?: boolean }[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-expand textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [userEdit, mode]);

  useEffect(() => {
    if (bulletState.status === BulletStatus.COACHING) {
      setMode("coaching");
    } else if (bulletState.status === BulletStatus.ACCEPTED) {
      setMode("completed");
    } else if (bulletState.status === BulletStatus.COMPLETED) {
      // COMPLETED means coaching is done, show the result inside coaching UI
      setMode("coaching");
    } else {
      setMode("initial");
      setUserEdit(bulletState.ai_suggestion || bulletState.original_bullet);
    }
  }, [bulletState, show]);

  useEffect(() => {
    if (mode === "coaching") {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [bulletState.chat_history, localMessages, mode]);

  const handleAction = async (action: "ACCEPT" | "REJECT" | "EDIT") => {
    Loader.showLoading("Processing...");
    try {
      const res = await service.handleBulletAction({
        session_id: sessionId,
        bullet_id: bulletState.bullet_id,
        action,
        user_edit: action === "EDIT" ? userEdit : undefined,
      });
      if (res.status === "SUCCESS") {
        onUpdate(res.bullet_state);
        if (action === "ACCEPT") setMode("completed");
        if (action === "REJECT") setMode("coaching");
        if (action === "EDIT") setMode("initial");
      }
    } catch (err) {
      console.error("Action failed", err);
    } finally {
      Loader.dismissLoading();
    }
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;
    const currentInput = userInput.trim();
    setUserInput("");

    const lowerInput = currentInput.toLowerCase();
    const affirmativeWords = ["good", "nice", "looks good", "ok", "fine", "perfect", "great", "yes", "accept", "looks nice", "awesome", "it's good", "its good", "better"];
    
    // Check if the user is just affirming
    if (affirmativeWords.includes(lowerInput) || (lowerInput.length < 15 && affirmativeWords.some(w => lowerInput.includes(w)))) {
      setLocalMessages([
        { role: "user", content: currentInput },
        { role: "bot", content: "I'm glad you like it! Would you like to accept this version?", isAffirmativePrompt: true }
      ]);
      return;
    }

    setLocalMessages([]); // clear any local messages
    Loader.showLoading("Thinking...");
    try {
      const res = await service.handleBulletChat({
        session_id: sessionId,
        bullet_id: bulletState.bullet_id,
        user_input: currentInput,
      });
      if (res.status === "SUCCESS") {
        onUpdate(res.bullet_state);
      }
    } catch (err) {
      console.error("Chat failed", err);
    } finally {
      Loader.dismissLoading();
    }
  };

  const allMessages = [...bulletState.chat_history, ...localMessages];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      contentClassName="rewrite-chat-modal"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title className="text-gradient">Optimize Achievement</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 bg-dark-custom overflow-hidden d-flex flex-column" style={{ maxHeight: "85vh" }}>
        {/* ── Sticky Top Panel: Current Suggestion ── */}
        {mode !== "coaching" && (
        <div className="sticky-preview-pane p-4 glass-panel border-0 border-bottom">
           <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-blue-bright text-uppercase fw-bold tracking-wider">Working Suggestion</small>
              {mode === "completed" && <span className="badge bg-success-soft text-success px-2 py-1">Applied ✨</span>}
           </div>
           <div className="current-suggestion-display">
              {bulletState.ai_suggestion || bulletState.original_bullet}
              {mode === "edit" && <div className="editing-indicator">Editing...</div>}
           </div>
           
           {mode !== "completed" && mode !== "edit" && (
            <div className="preview-actions-row mt-4 animate-slide-up">
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="action-btn-glow px-4"
                  onClick={() => handleAction("ACCEPT")}
                >
                  Accept & Apply
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => setMode("edit")}
                >
                   Manual Edit
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => handleAction("REJECT")}
                >
                  🚀 Coach Me
                </Button>
             </div>
           )}
        </div>
        )}

        {/* ── Scrollable Interaction Area ── */}
        <div className="flex-grow-1 overflow-auto p-4 custom-scrollbar">
          
          {/* ── Original Context (Collapsible small) ── */}
          <div className="original-context-mini mb-4">
             <small className="text-blue-muted text-uppercase fw-bold">Original: </small>
             <span className="text-silver-muted small italic">{bulletState.original_bullet}</span>
          </div>

          {/* ── Editing Mode ── */}
          {mode === "edit" && (
            <div className="animation-fade-in">
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={2}
                  ref={textareaRef}
                  className="custom-edit-textarea-v2"
                  value={userEdit}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setUserEdit(e.target.value)}
                  autoFocus
                />
                <div className="d-flex justify-content-between align-items-center mt-3">
                   <small className="text-blue-muted">AI will polish your manual edits after you click Refine.</small>
                   <div className="d-flex gap-2">
                      <Button variant="outline-secondary" size="sm" onClick={() => setMode("initial")}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={() => handleAction("EDIT")}>Refine with AI</Button>
                   </div>
                </div>
              </Form.Group>
            </div>
          )}

          {/* ── Coaching Mode (Chat) ── */}
          {mode === "coaching" && (
            <div className="coaching-chat-area">
              <div className="chat-messages">
                {allMessages.length === 0 && (
                  <div className="text-center py-5 mb-3 glass-panel border-dashed">
                     <div className="coach-avatar-mini mb-3">🤖</div>
                     <h6 className="text-white mb-2">JozAI Coach Ready</h6>
                     <p className="text-blue-muted small m-0 px-4">
                        How can I help you perfect this achievement? I can focus on <strong>metrics</strong>, 
                        <strong>technical tools</strong>, or making it more <strong>action-oriented</strong>.
                     </p>
                  </div>
                )}
                {allMessages.map((msg, i) => {
                  const isBot = msg.role === "bot";
                  const showActions = isBot && ((msg as any).isAffirmativePrompt || (i === allMessages.length - 1 && i > 0));
                  return (
                    <div key={i} className={`chat-bubble-v2 ${isBot ? "bot" : "user"}`}>
                      <div className="bubble-header">
                        <small className="role-tag">{isBot ? "JozAI" : "You"}</small>
                      </div>
                      <div className="bubble-body">{msg.content}</div>
                      {showActions && (
                        <div className="mt-3 d-flex gap-2">
                          <Button variant="success" size="sm" onClick={() => handleAction("ACCEPT")}>Accept Suggestion</Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => setMode("edit")}>Manual Edit</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
            </div>
          )}

          {/* ── Completed Mode ── */}
          {mode === "completed" && (
            <div className="animation-fade-in text-center py-5">
              <div className="success-lottie-placeholder mb-3">✔️</div>
              <h4 className="text-white mb-2">Ready to Go!</h4>
              <p className="text-silver-muted small">This bullet point has been successfully optimized and added to your strategic plan.</p>
              <Button variant="outline-primary" className="mt-4 px-5" onClick={onHide}>Done</Button>
            </div>
          )}
        </div>

        {/* ── Persistent Chat Input (Fixed at bottom) ── */}
        {mode === "coaching" && (
          <div className="chat-input-sticky shadow-lg p-3 glass-panel border-top-0">
             <div className="chat-input-row">
                <Form.Control
                  type="text"
                  placeholder="Ask for changes or add context (e.g., 'Make it more technical')"
                  className="premium-chat-input"
                  value={userInput}
                  autoComplete="off"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setUserInput(e.target.value)}
                  onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleSendMessage()}
                />
                <Button className="premium-send-btn" onClick={handleSendMessage} disabled={!userInput.trim()}>
                   <span className="send-arrow">➤</span>
                </Button>
             </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RewriteChatModal;

